/* eslint-disable no-unused-vars */
/* eslint-disable react/style-prop-object */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Fieldset,
  Input,
  ButtonIcon,
  Sheet,
  DialogNew,
  Table,
  Textarea,
  SearchPicker,
  Dropdown,
  Spinner,
  Icon,
} from '@optimizely/axiom';
import colors from '@optimizely/design-tokens/dist/json/colors.json';

import {
  fetchTeamUsers,
  fetchAccountUsers,
  createTeam,
  updateTeam,
} from '../../api/userApi';
import { searchFunction, formatDate } from '../utils';

const EditTeam = ({
  setDialogOpen,
  accountId,
  selectedTeam = null,
  setSelectedTeam,
  onClose, // Receive the callback as a prop
  showFeedback,
}) => {
  const [team, setTeam] = useState({
    name: '',
    description: '',
  });
  const [data, setData] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [accountData, setAccountData] = useState([]);
  const [accountError, setAccountError] = useState(null);
  const [teamError, setTeamError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);

  const [error, setError] = useState(null);

  const onSave = async () => {
    setError(null);
    try {
      if (selectedTeam) {
        const teamData = {
          name: team.name,
          description: team.description,
          external_ref: team.external_ref,
          members_added: selectedUsers.map((user) => user.id),
          members_removed: deletedUsers.map((user) => user.id),
        };
        //result is  not used, but kept for future use
        const result = await updateTeam(accountId, selectedTeam.id, teamData);
        showFeedback(`Team "${team.name}" successfully updated`);
      } else {
        const teamData = {
          name: team.name,
          description: team.description,
          members: selectedUsers.map((user) => user.id),
        };
        //result is  not used, but kept for future use
        const result = await createTeam(accountId, teamData);
        showFeedback(`Team "${team.name}" successfully created`);
      }

      setDialogOpen(false);
      onClose(); // Call the callback to fetch updated teams data
    } catch (error) {
      let errorMessage = 'Unable to save changes.';

      try {
        // Parse the error message as JSON, coming from the backend
        const errorObject = JSON.parse(error.message);
        errorMessage = errorObject && errorObject.message
          ? errorObject.message
          : errorMessage;
      } catch (parseError) {
        console.log('Error parsing error message:', parseError);
      }

      setError(errorMessage);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setTeam((prevTeam) => ({
      ...prevTeam,
      [id]: value,
    }));
  };

  const handleSearch = (queryObject) => {
    return new Promise((resolve, reject) => {
      const { query } = queryObject; // Extract the query string from the queryObject

      if (typeof query !== 'string') {
        return reject(new Error('query must be a string'));
      }
      // Create a map of data items for quick lookup
      const dataMap = new Map(data.map((dataItem) => [dataItem.id, true]));

      // Filter accountData to exclude users already in data (team users)
      const userAvailableEntitiesData = accountData.filter(
        (accountItem) => !dataMap.has(accountItem.id),
      );

      searchFunction(userAvailableEntitiesData, query)
        .then((results) => {
          setSearchResults(results);
          resolve(results);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  };

  useEffect(() => {
    const fetchAccountUsersData = async () => {
      setAccountError(null);
      try {
        const accountResult = await fetchAccountUsers(accountId);
        setAccountData(accountResult);
      } catch (err) {
        setAccountError('Unable to retrieve account users.');
      }
    };

    const fetchTeamUsersData = async () => {
      setTeamError(null);
      try {
        const teamResult = await fetchTeamUsers(accountId, selectedTeam.id);
        setData(teamResult.members);
      } catch (err) {
        setTeamError('Unable to retrieve team members.');
      }
    };

    fetchAccountUsersData();

    if (selectedTeam) {
      setTeam(selectedTeam);
      fetchTeamUsersData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, selectedTeam]);

  return (
    <>
      <Sheet
        testSection="edit-team-dialog"
        hasRequiredFieldsIndicator={true}
        footerButtonList={[
          <Button
            key={0}
            onClick={() => {
              setDialogOpen(false);
              setSelectedTeam(null);
            }}>
            Cancel
          </Button>,
          <Button key={1} onClick={onSave} style="highlight">
            Save
          </Button>,
        ]}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTeam(null);
        }}
        title={selectedTeam ? 'Edit Team' : 'New Team'}>
        <>
          {selectedTeam && (
            <fieldset>
              <Table
                density="tight"
                style="rule-no-bottom-border"
                tableLayoutAlgorithm="auto">
                <Table.THead>
                  <Table.TR noBorder={true}>
                    <Table.TH width="20%">Members</Table.TH>
                    <Table.TH width="20%">Created</Table.TH>
                  </Table.TR>
                </Table.THead>
                <Table.TBody>
                  <Table.TR>
                    <Table.TD width="20%">{team.members_count}</Table.TD>
                    <Table.TD width="20%">{formatDate(team.created)}</Table.TD>
                  </Table.TR>
                </Table.TBody>
              </Table>
            </fieldset>
          )}
          <fieldset>
            <Input
              id="name"
              label="Name"
              isRequired={true}
              type="text"
              value={team ? team.name : ''}
              onChange={handleInputChange}
            />
          </fieldset>
          <fieldset>
            <Textarea
              id="description"
              label="Description"
              type="text"
              value={team ? team.description : ''}
              onChange={handleInputChange}
            />
          </fieldset>
        </>

        <Fieldset title={selectedTeam ? 'Team Members' : 'Add Users'}>
          <p>Search for users to add to this team.</p>
          <SearchPicker searchFunction={handleSearch} supportedTypes={['user']}>
            {({
              availableEntities,
              renderInput,
              isLoading,
              resultsText,
              searchQuery,
            }) => {
              const hasEmptySearchResults =
                searchQuery === '' && availableEntities.length === 0;
              return (
                <Dropdown
                  width={600}
                  fullWidth={true}
                  renderActivator={renderInput}
                  shouldHideChildrenOnClick={false}>
                  {({ handleHideChildren }) => (
                    <Dropdown.Contents minWidth={600} isLoading={isLoading}>
                      {!hasEmptySearchResults && (
                        <Dropdown.ListItem ignoreToggle={true}>
                          <span className="micro muted soft--sides">
                            {resultsText.summary}
                          </span>
                        </Dropdown.ListItem>
                      )}
                      {availableEntities
                        .filter(
                          (item) =>
                            !selectedUsers.some(
                              (selectedUser) => selectedUser.id === item.id,
                            ),
                        )
                        .map((item, index) => (
                          <Dropdown.ListItem key={index}>
                            <Dropdown.BlockLink
                              hideFocusCheckIcon={true}
                              onClick={() => {
                                setSelectedUsers((prevSelectedUsers) => {
                                  // Check if the item already exists in the selectedUsers array
                                  const itemExists = prevSelectedUsers.some(
                                    (selectedUser) =>
                                      selectedUser.id === item.id,
                                  );
                                  // If the item doesn't exist, add it to the selectedUsers array
                                  if (!itemExists) {
                                    return [...prevSelectedUsers, item];
                                  }
                                  // If the item exists, return the previous selectedUsers array unchanged
                                  return prevSelectedUsers;
                                });
                                setData((prevData) => {
                                  // Check if the item already exists in the data
                                  const itemExists = prevData.some(
                                    (dataItem) => dataItem.id === item.id,
                                  );
                                  // If the item doesn't exist, add it to the data
                                  if (!itemExists) {
                                    return [...prevData, item];
                                  }
                                  // If the item exists, return the previous data unchanged
                                  return prevData;
                                });
                                handleHideChildren();
                                searchQuery = '';
                              }}>
                              <Dropdown.BlockLinkText
                                text={`${
                                  item.first_name ? item.first_name : ''
                                } ${
                                  item.last_name ? item.last_name : ''
                                }`.trim()}
                              />
                              <Dropdown.BlockLinkSecondaryText
                                secondaryText={item.email}
                              />
                            </Dropdown.BlockLink>
                          </Dropdown.ListItem>
                        ))}
                      {isLoading && <Spinner hasOverlay={true} />}
                    </Dropdown.Contents>
                  )}
                </Dropdown>
              );
            }}
          </SearchPicker>
        </Fieldset>

        <Fieldset title="" titleSize="small">
          {(data && data.length > 0) ||
          (selectedUsers && selectedUsers.length > 0) ? (
            <Table density={'loose'} tableLayoutAlgorithm="auto">
              <Table.THead>
                <Table.TR>
                  <Table.TH></Table.TH>
                  <Table.TH>First Name</Table.TH>
                  <Table.TH>Last Name</Table.TH>
                  <Table.TH>Email</Table.TH>
                  <Table.TH>Organization Role</Table.TH>
                  <Table.TH></Table.TH>
                </Table.TR>
              </Table.THead>
              <Table.TBody>
                {data &&
                  data.map((user) => (
                    <Table.TR
                      key={user.id}
                      className={deletedUsers.includes(user) ? 'muted' : ''}>
                      <Table.TD>
                        {selectedUsers.some(
                          (selectedUser) => selectedUser.id === user.id,
                        ) && (
                          <Icon
                            name="circle-small-solid"
                            size="small"
                            color={colors.brandBlueDark}
                          />
                        )}
                      </Table.TD>
                      <Table.TD>{`${
                        user.first_name ? user.first_name : ''
                      }`}</Table.TD>
                      <Table.TD>{`${
                        user.last_name ? user.last_name : ''
                      }`}</Table.TD>
                      <Table.TD>{user.email}</Table.TD>
                      <Table.TD>{user.org_role}</Table.TD>
                      <Table.TD textAlign="right">
                        <div className="text--right">
                          <Dropdown
                            placement={'bottom-end'}
                            renderActivator={({
                              onClick,
                              buttonRef,
                              onBlur,
                            }) => (
                              <ButtonIcon
                                iconFill="default"
                                iconName="ellipsis-solid"
                                buttonRef={buttonRef}
                                onBlur={onBlur}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onClick(e);
                                }}
                                size="small"
                                style="plain"
                                testSection="team-user-more-options"
                                title="More options"
                              />
                            )}
                            shouldHideChildrenOnClick={true}
                            shouldHideChildrenOnTriggerBlur={true}
                            width={200}>
                            <Dropdown.Contents>
                              {deletedUsers.includes(user) ? (
                                <Dropdown.ListItem>
                                  <Dropdown.BlockLink
                                    onClick={() => {
                                      setDeletedUsers((prevDeletedUsers) =>
                                        prevDeletedUsers.filter(
                                          (deletedUser) =>
                                            deletedUser.id !== user.id,
                                        ),
                                      );
                                    }}>
                                    <Dropdown.BlockLinkText
                                      testSection="restore-user"
                                      text="Restore"
                                    />
                                  </Dropdown.BlockLink>
                                </Dropdown.ListItem>
                              ) : (
                                <Dropdown.ListItem>
                                  <Dropdown.BlockLink
                                    onClick={() => {
                                      if (
                                        selectedUsers.some(
                                          (selectedUser) =>
                                            selectedUser.id === user.id,
                                        )
                                      ) {
                                        setSelectedUsers((prevSelectedUsers) =>
                                          prevSelectedUsers.filter(
                                            (selectedUser) =>
                                              selectedUser.id !== user.id,
                                          ),
                                        );
                                        setData((prevData) =>
                                          prevData.filter(
                                            (dataUser) =>
                                              dataUser.id !== user.id,
                                          ),
                                        );
                                      } else {
                                        setDeletedUsers((prevDeletedUsers) => [
                                          ...prevDeletedUsers,
                                          user,
                                        ]);
                                      }
                                    }}>
                                    <Dropdown.BlockLinkText
                                      isDestructive
                                      testSection="remove-user"
                                      text="Remove"
                                    />
                                  </Dropdown.BlockLink>
                                </Dropdown.ListItem>
                              )}
                            </Dropdown.Contents>
                          </Dropdown>
                        </div>
                      </Table.TD>
                    </Table.TR>
                  ))}
              </Table.TBody>
            </Table>
          ) : (
            <div className="text--center">
              <h6>No users</h6>
              <p className="muted">
                Start by searching for users here to add them to this team
              </p>
            </div>
          )}
        </Fieldset>
      </Sheet>
        {(accountError || teamError || error) && (
          <DialogNew
            title={accountError ? "Loading issue" : 
              teamError ? "Loading issue" : 
              "Saving issue"}
            testSection="error-modal-edit-team"
            hasCloseButton={true}
            onClose={() => {
              setAccountError(null);
              setTeamError(null);
              setError(null);
              setDialogOpen(false);
              setSelectedTeam(null);
            }}
            footerButtonList={[
              <Button
                key="close"
                style="highlight"
                onClick={() => {
                  setDialogOpen(false);
                  setAccountError(null);
                  setTeamError(null);
                  setError(null);
                  setSelectedTeam(null);
                }}>
                Close
              </Button>,
            ]}>
            <p>{accountError || teamError || error}</p>
          </DialogNew>
        )}
    </>
  );
};

export default EditTeam;
